import { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfig } from '../../hooks/useConfig';
import { defaultTestData } from '../../data/defaultTestData';
import { useCachedFxRange } from '../../hooks/useCachedData';
import './LabConfigSettings.css';

const TABS = [
    { key: 'labDefaults', label: 'Lab Defaults' },
    { key: 'fxRanges', label: 'Ideal Ranges' },
];

function buildFxRangeTemplate(fxRangeData) {
    if (!fxRangeData?.body) return JSON.stringify({}, null, 2);
    const flat = {};
    const addEntries = (obj) => {
        if (!obj) return;
        for (const [name, range] of Object.entries(obj)) {
            if (!flat[name]) flat[name] = { fx_low: range.fx_low, fx_high: range.fx_high };
        }
    };
    addEntries(fxRangeData.body.generic);
    const special = fxRangeData.body.special;
    if (special) {
        for (const sexCat of Object.values(special)) {
            addEntries(sexCat.generic);
            addEntries(sexCat.menopausal);
            addEntries(sexCat.premenopausal);
        }
    }
    const sorted = Object.keys(flat).sort().reduce((acc, k) => { acc[k] = flat[k]; return acc; }, {});
    return JSON.stringify(sorted, null, 2);
}

function buildTemplate(sectionKey, fxRangeData) {
    if (sectionKey === 'labDefaults') {
        return JSON.stringify(defaultTestData, null, 2);
    }
    if (sectionKey === 'fxRanges') {
        return buildFxRangeTemplate(fxRangeData);
    }
    return '{}';
}

export default function LabConfigSettings() {
    const { config, saveConfig, resetSection, hasChanges } = useConfig();
    const { fxRangeData } = useCachedFxRange();
    const [activeTab, setActiveTab] = useState('labDefaults');
    const [editorText, setEditorText] = useState('');
    const [parseError, setParseError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [statusMsg, setStatusMsg] = useState(null);

    const template = useMemo(() => buildTemplate(activeTab, fxRangeData), [activeTab, fxRangeData]);

    const hasCustom = config && config[activeTab] && Object.keys(config[activeTab]).length > 0;

    useEffect(() => {
        if (hasCustom) {
            setEditorText(JSON.stringify(config[activeTab], null, 2));
        } else {
            setEditorText(template);
        }
        setParseError(null);
        setStatusMsg(null);
    }, [activeTab, config, hasCustom, template]);

    const handleTextChange = useCallback((e) => {
        const val = e.target.value;
        setEditorText(val);
        setStatusMsg(null);
        try {
            JSON.parse(val);
            setParseError(null);
        } catch (err) {
            setParseError(err.message);
        }
    }, []);

    const isDirty = useMemo(() => {
        try {
            const currentParsed = JSON.parse(editorText);
            const savedValue = hasCustom ? config[activeTab] : null;
            if (!savedValue) {
                return JSON.stringify(currentParsed) !== JSON.stringify(JSON.parse(template));
            }
            return JSON.stringify(currentParsed) !== JSON.stringify(savedValue);
        } catch {
            return false;
        }
    }, [editorText, config, activeTab, hasCustom, template]);

    const handleSave = async () => {
        if (parseError) return;
        setSaving(true);
        setStatusMsg(null);
        try {
            const parsed = JSON.parse(editorText);

            if (JSON.stringify(parsed) === JSON.stringify(JSON.parse(template))) {
                setStatusMsg('No changes from defaults — nothing to save.');
                setSaving(false);
                return;
            }

            const newConfig = { version: 1, ...config, [activeTab]: parsed };
            await saveConfig(newConfig);
            setStatusMsg('Saved successfully.');
        } catch (err) {
            setStatusMsg('Error saving: ' + (err.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!hasCustom) return;
        setResetting(true);
        setStatusMsg(null);
        try {
            await resetSection(activeTab);
            setEditorText(template);
            setStatusMsg('Reset to defaults.');
        } catch (err) {
            setStatusMsg('Error resetting: ' + (err.message || 'Unknown error'));
        } finally {
            setResetting(false);
        }
    };

    return (
        <div className="lab-config-settings">
            <h3>Lab Configuration</h3>
            <p className="lab-config-desc">
                Customize default lab reference ranges and ideal ranges.
                {hasCustom && <span className="lab-config-badge">Custom</span>}
            </p>

            <div className="lab-config-tabs">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`lab-config-tab ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <textarea
                className={`lab-config-editor ${parseError ? 'error' : ''}`}
                value={editorText}
                onChange={handleTextChange}
                spellCheck={false}
            />

            {parseError && (
                <div className="lab-config-parse-error">Invalid JSON: {parseError}</div>
            )}

            {statusMsg && (
                <div className="lab-config-status">{statusMsg}</div>
            )}

            <div className="lab-config-actions">
                <button
                    className="lab-config-btn save"
                    onClick={handleSave}
                    disabled={!!parseError || !isDirty || saving}
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                    className="lab-config-btn reset"
                    onClick={handleReset}
                    disabled={!hasCustom || resetting}
                >
                    {resetting ? 'Resetting...' : 'Reset to Defaults'}
                </button>
            </div>
        </div>
    );
}
